import { ChangeDetectionStrategy, Component, Inject, Optional } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BaseCellRendererComponent, Params } from '@y42/ag-grid';
import { connectState, trackByValue } from '@y42/angular/util';
import { plusIcon } from '@y42/icons';
import { Y42FeatureType } from '@y42/json-schema';
import { IRowNode } from 'ag-grid-community';
import { map, of, startWith, switchMap } from 'rxjs';
import { FEATURE_TYPE, FeatureTagsQueryService } from '../../../api';

export interface TagsCellRendererParams<T = unknown> {
  showWhenIsNotGroup?: boolean;
  editable?: boolean;
  onUpdateTags?: (node: IRowNode<T>, tags: string[]) => void;
}
export type TagsCellRendererParamsValue = string[];

type ParamsType<TData> = Params<TagsCellRendererParamsValue, TData, TagsCellRendererParams>;

@UntilDestroy()
@Component({
  selector: 'y42-tags-cell-renderer',
  templateUrl: './tags-cell-renderer.component.html',
  styleUrls: ['./tags-cell-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagsCellRendererComponent<TData = unknown> extends BaseCellRendererComponent<
  TagsCellRendererParamsValue,
  TData,
  ParamsType<TData>
> {
  constructor(
    @Inject(FEATURE_TYPE) @Optional() private readonly featureType: Y42FeatureType | null,
    private readonly featureTagsQueryService: FeatureTagsQueryService,
  ) {
    super();
  }

  readonly plusIcon = plusIcon;

  readonly trackByValue = trackByValue;

  readonly state = connectState(this, {
    params: this.params$,
    tags: this.params$.pipe(map((params) => params.value || [])),
    areTagsVisible: this.params$.pipe(
      map((params) => !!params.node.group || !!params.showWhenIsNotGroup),
      startWith(false),
    ),
    editable: this.params$.pipe(
      map((params) => (!!params.node.group || !!params.showWhenIsNotGroup) && !!params.editable),
      startWith(false),
    ),
    featureTypeTags: of(this.featureType).pipe(
      switchMap((featureType) => (featureType ? this.featureTagsQueryService.getTagsByFeatureType(featureType) : [])),
      map((tags) => [...new Set(tags)]),
      startWith([] as string[]),
    ),
  });

  onSelectItems(tags: string[]) {
    this.add(tags[0] || '');
  }

  add(newTag: string) {
    const tag = (newTag || '').trim();

    if (!tag) {
      return;
    }

    if (this.state.tags.includes(tag)) {
      return;
    }

    const tags = [...this.state.tags, tag];
    this.state.params.node.setDataValue('tags', tags);
    this.state.params.onUpdateTags?.(this.state.params.node, tags);
  }

  remove(removedTag: string): void {
    const tags = this.state.tags.filter((tag) => tag !== removedTag);
    this.state.params.node.setDataValue('tags', tags);
    this.state.params.onUpdateTags?.(this.state.params.node, tags);
  }
}
